
interface Post {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  category: string;
  featured: boolean;
  published: boolean;
  bestseller: boolean;
  our_pick: boolean;
  rating: number;
  cover_image: string;
  created_at: string;
  updated_at: string;
}

interface PostsListProps {
  posts: Post[];
  onEdit: (post: Post) => void;
  onDelete: (postId: string) => void;
}

export default function PostsList({ posts, onEdit, onDelete }: PostsListProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`ri-star-${star <= rating ? 'fill' : 'line'} text-sm ${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
          ></i>
        ))}
        <span className="ml-1 text-xs text-gray-600">
          {rating > 0 ? `${rating}/5` : 'No rating'}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Posts ({posts.length})
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Manage your literary review posts and articles.
        </p>
      </div>
      
      {posts.length === 0 ? (
        <div className="text-center py-12">
          <i className="ri-file-text-line text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
          <p className="text-gray-500">Get started by creating your first post.</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {posts.map((post) => (
            <li key={post.id} className="px-4 py-4 sm:px-6">
              <div className="flex items-start justify-between">
                <div className="flex space-x-4 flex-1 min-w-0">
                  {post.cover_image && (
                    <div className="flex-shrink-0">
                      <img
                        src={post.cover_image}
                        alt={`${post.title} cover`}
                        className="w-16 h-24 object-cover rounded-md shadow-sm"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-lg font-medium text-gray-900 truncate">
                        {post.title}
                      </h4>
                      <div className="flex space-x-2">
                        {post.our_pick && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <span className="mr-1">🍁</span>
                            Our Pick
                          </span>
                        )}
                        {post.bestseller && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <i className="ri-award-fill mr-1"></i>
                            Bestseller
                          </span>
                        )}
                        {post.featured && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Featured
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            post.published
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                      <span>By {post.author}</span>
                      <span>•</span>
                      <span>{post.category}</span>
                      <span>•</span>
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                    <div className="mt-2">
                      {renderStars(post.rating || 0)}
                    </div>
                    {post.excerpt && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {post.excerpt}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => onEdit(post)}
                    className="text-indigo-600 hover:text-indigo-900 p-2 rounded-md hover:bg-indigo-50 cursor-pointer"
                    title="Edit post"
                  >
                    <i className="ri-edit-line w-5 h-5"></i>
                  </button>
                  <button
                    onClick={() => onDelete(post.id)}
                    className="text-red-600 hover:text-red-900 p-2 rounded-md hover:bg-red-50 cursor-pointer"
                    title="Delete post"
                  >
                    <i className="ri-delete-bin-line w-5 h-5"></i>
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
